// src/utils/lazyImport.tsx
import React, { lazy, ComponentType } from 'react';

// Type definitions with proper constraints
type ImportFactory<T extends ComponentType<any>> = () => Promise<{ default: T }>;
type NamedImportFactory<T, K extends keyof T> = () => Promise<{ [P in K]: T[P] }>;

/**
 * Creates an error fallback component for lazy loading errors
 */
export function createErrorFallback(componentName: string): ComponentType {
  return () => (
    <div className="flex flex-col items-center justify-center p-8 text-center rounded-xl glass-card border border-gray-300/50 bg-white/30 backdrop-blur-[100px] shadow-md">
      <h2 className="text-xl font-bold mb-3">Getting your trading tools ready...</h2>
      <p className="text-gray-600 mb-4">
        Almost there! Your {componentName} is loading.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
      >
        Continue
      </button>
    </div>
  );
}

/**
 * Helper for lazy-loading components with consistent error handling
 * @param factory Import factory function
 * @param componentName Name of component for error display
 */
export function lazyImport<T extends ComponentType<any>>(
  factory: ImportFactory<T>,
  componentName: string
) {
  return lazy(() => 
    factory().catch(() => ({
      default: createErrorFallback(componentName)
    }))
  );
}

/**
 * Helper for lazy-loading named exports with consistent error handling
 * @param factory Import factory function
 * @param exportName Name of the export to be used
 * @param componentName Name of component for error display
 */
export function lazyNamedImport<T, K extends keyof T>(
  factory: NamedImportFactory<T, K>,
  exportName: K,
  componentName: string
) {
  return lazy(() => 
    factory()
      .then(module => ({ 
        default: module[exportName] as unknown as ComponentType<any>
      }))
      .catch(() => ({
        default: createErrorFallback(componentName)
      }))
  );
}